/**
 * The organization's workflows — the graph, and the step being edited.
 *
 * What a workflow *is* belongs to the app: a chain of instructions a task is
 * driven by, drawn on a canvas, run a step at a time. What this page adds is
 * that the chain travels — an organization shares one list with every member,
 * so somebody who joins finds the team's workflows already there rather than
 * drawing them again.
 *
 * **One list, not one per workspace.** A chain called *Feature* is the same
 * chain whichever repositories a task touches, which is why no step here names
 * a project: a check is a scope — what the task changed, or all of it — and the
 * command belongs to the project it runs in.
 *
 * Nothing here saves on its own. Every edit goes into the draft and lands when
 * the header's **Publish** is pressed: that is the verb that commits, so it is
 * the one that keeps the ink. *Add a step* is framed and not filled, because it
 * changes a draft and nothing else — the same distinction the rest of this area
 * makes.
 *
 * **Import from Airlock** takes a workflow a member's app copied (owner,
 * 2026-10-31: « partager les workflows dans l'organisation »): pasted, read with
 * the organization workflows' validator (`parseOrgWorkflows`), it replaces the
 * one of its name or is added — into the draft, published with the rest. The
 * app never writes here itself: an install only reads the organization's
 * workflows, and an administrator decides.
 */
import { useState } from 'react';
import type React from 'react';
import { MAX_STEPS, MAX_WORKFLOWS, NO_CHECK, STEP_RUN_AS } from '@airlock/contracts/workflow/sanitize';
import { color, font, radius } from '../../../theme/tokens';
import { HeaderButton } from '../../../components/Shell';
import { Modal } from '../../../components/Modal';
import { Note, PageLede } from '../../../components/list';
import type { DocWorkflow, WorkflowEdge, WorkflowStep } from '../../../lib/types';
import { newRowId } from '../shared';
import { WHEN_NEXT, WorkflowCanvas } from './canvas';
import { StepInspector } from './inspector';
import { importedWorkflows } from './import';
import { SIDE_PANEL_WIDTH } from '../../../components/SidePanel';

/** Where a new step lands: to the right of the last one, on its row. */
function placeAfter(steps: WorkflowStep[]): { x: number; y: number } {
  if (!steps.length) return { x: 44, y: 72 };
  const last = steps[steps.length - 1];
  return { x: last.x + 266, y: last.y };
}

function newStep(steps: WorkflowStep[]): WorkflowStep {
  return {
    id: newRowId('st'),
    name: '',
    instruction: '',
    // Never blank: a step that named no model used to fall back to the task's,
    // which made one control silently govern some steps and not others.
    model: STEP_RUN_AS.model,
    effort: STEP_RUN_AS.effort,
    agent: STEP_RUN_AS.agent,
    conversation: 'main',
    annotate: false,
    gate: 'auto',
    check: { ...NO_CHECK },
    ...placeAfter(steps)
  };
}

export function Workflows({
  workflows,
  editable,
  onChange
}: {
  workflows: DocWorkflow[];
  editable: boolean;
  onChange: (next: DocWorkflow[]) => void;
}): React.JSX.Element {
  const [openId, setOpenId] = useState<string | null>(workflows[0]?.id ?? null);
  const [selected, setSelected] = useState<string | null>(null);
  // The name being typed, or `null` while nothing is being named — and whether
  // the deletion is being confirmed. Both were one of the browser's grey boxes,
  // which is the one part of this area nobody designed.
  const [naming, setNaming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  /** The pasted workflow, or `null` while nothing is being imported — and why the last paste was refused. */
  const [importing, setImporting] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);

  const open = workflows.find((one) => one.id === openId) ?? workflows[0] ?? null;
  const step = open?.steps.find((one) => one.id === selected) ?? null;

  const write = (patch: Partial<DocWorkflow>): void => {
    if (!open) return;
    onChange(workflows.map((one) => (one.id === open.id ? { ...one, ...patch } : one)));
  };

  const addWorkflow = (): void => {
    const name = (naming ?? '').trim();
    if (!name) return;
    const made: DocWorkflow = { id: newRowId('wf'), name, sortOrder: workflows.length, steps: [], edges: [] };
    onChange([...workflows, made]);
    setOpenId(made.id);
    setSelected(null);
    setNaming(null);
  };

  const importWorkflow = (): void => {
    const got = importedWorkflows(workflows, importing ?? '', () => newRowId('wf'));
    if (typeof got === 'string') {
      setRefused(got);
      return;
    }
    onChange(got.workflows);
    setOpenId(got.id);
    setSelected(null);
    setImporting(null);
    setRefused(null);
  };

  const removeWorkflow = (): void => {
    if (!open) return;
    onChange(workflows.filter((one) => one.id !== open.id));
    setOpenId(workflows.find((one) => one.id !== open.id)?.id ?? null);
    setSelected(null);
    setDeleting(false);
  };

  const addStep = (): void => {
    if (!open) return;
    const made = newStep(open.steps);
    // Joined to the one before it, because a step nothing points at is a step
    // that is never reached — and the canvas would say so in amber rather than
    // leaving somebody to wonder.
    const last = open.steps[open.steps.length - 1];
    const edges: WorkflowEdge[] = last
      ? [...open.edges, { id: newRowId('ed'), source: last.id, target: made.id, when: 'always' }]
      : open.edges;
    write({ steps: [...open.steps, made], edges });
    setSelected(made.id);
  };

  const removeStep = (stepId: string): void => {
    if (!open) return;
    write({
      steps: open.steps.filter((one) => one.id !== stepId),
      // An arrow naming a step that is gone is an arrow to nowhere, so it goes
      // with it rather than being left to fail validation later.
      edges: open.edges.filter((edge) => edge.source !== stepId && edge.target !== stepId)
    });
    setSelected(null);
  };

  const link = (source: string, target: string): void => {
    if (!open || open.edges.some((edge) => edge.source === source && edge.target === target)) return;
    write({ edges: [...open.edges, { id: newRowId('ed'), source, target, when: 'always' }] });
  };

  return (
    <>
      {naming !== null && (
        <Modal
          title="A new workflow"
          lede="A chain of steps a task is driven through without being asked twice. Nothing is shared until this page is published."
          label="What is it called?"
          value={naming}
          placeholder="Feature"
          onChange={setNaming}
          confirm="Create the workflow"
          onConfirm={addWorkflow}
          onCancel={() => setNaming(null)}
        />
      )}

      {importing !== null && (
        <Modal
          title="Import a workflow from Airlock"
          lede="Paste what the app copied with Share with the organization. A workflow of the same name is replaced; nothing is shared until this page is published."
          label="The workflow"
          value={importing}
          placeholder='{ "name": "Fix and review", "steps": […], "edges": […] }'
          onChange={(next) => {
            setImporting(next);
            setRefused(null);
          }}
          problem={refused}
          confirm="Import the workflow"
          onConfirm={importWorkflow}
          onCancel={() => {
            setImporting(null);
            setRefused(null);
          }}
        />
      )}

      {deleting && open && (
        <Modal
          title={`Delete ${open.name || 'this workflow'}?`}
          lede={`Its ${open.steps.length} step${open.steps.length === 1 ? '' : 's'} go with it, and members stop getting it at their next check — once this page is published. Nothing is lost until then.`}
          confirm="Delete the workflow"
          danger
          onConfirm={removeWorkflow}
          onCancel={() => setDeleting(false)}
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '20px var(--al-gutter) 0' }}>
        <PageLede>
          The steps a task is driven by without being asked twice, and what each one is run as. Drawn here and in the
          app — one list for the whole organization, in every workspace its members hold.
        </PageLede>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <select
            value={open?.id ?? ''}
            onChange={(event) => {
              setOpenId(event.target.value);
              setSelected(null);
            }}
            aria-label="Which workflow"
            style={{
              height: 32,
              minWidth: 220,
              padding: '0 10px',
              border: `1px solid ${color.lineInput}`,
              borderRadius: radius.md,
              background: color.surface,
              fontFamily: 'inherit',
              fontSize: 13,
              color: color.ink
            }}
          >
            {workflows.length === 0 && <option value="">No workflow yet</option>}
            {workflows.map((one) => (
              <option key={one.id} value={one.id}>
                {one.name || 'Unnamed workflow'}
              </option>
            ))}
          </select>
          {editable && (
            <HeaderButton onClick={() => setNaming('')} disabled={workflows.length >= MAX_WORKFLOWS}>
              New workflow
            </HeaderButton>
          )}
          {editable && <HeaderButton onClick={() => setImporting('')}>Import from Airlock</HeaderButton>}
          {editable && open && (
            <HeaderButton onClick={addStep} disabled={open.steps.length >= MAX_STEPS}>
              Add a step
            </HeaderButton>
          )}
          {editable && open && <HeaderButton onClick={() => setDeleting(true)}>Delete</HeaderButton>}
          <span style={{ flex: 1 }} />
          {open && (
            <span style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint }}>
              {open.steps.length} step{open.steps.length === 1 ? '' : 's'} · {open.edges.length} arrow
              {open.edges.length === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {!editable && <Note>These workflows are the owner’s and the administrators’. The canvas shows them; nothing on it saves.</Note>}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          minHeight: 0,
          marginTop: 14,
          // The page leaves the panel's width free so nothing passes under it (§45.8).
          marginRight: open && step ? SIDE_PANEL_WIDTH : 0,
          boxShadow: `inset 0 1px 0 ${color.line}`
        }}
      >
        {open ? (
          <WorkflowCanvas
            steps={open.steps}
            edges={open.edges}
            selected={selected}
            editable={editable}
            onSelect={setSelected}
            onMove={(stepId, x, y) =>
              write({ steps: open.steps.map((one) => (one.id === stepId ? { ...one, x: Math.round(x), y: Math.round(y) } : one)) })
            }
            onCycle={(edgeId) =>
              write({ edges: open.edges.map((edge) => (edge.id === edgeId ? { ...edge, when: WHEN_NEXT[edge.when] } : edge)) })
            }
            onUnlink={(edgeId) => write({ edges: open.edges.filter((edge) => edge.id !== edgeId) })}
          />
        ) : (
          <p style={{ flex: 1, padding: 24, margin: 0, fontSize: 13, color: color.inkFaint }}>
This organization shares no workflow yet.
          </p>
        )}

        {open && step && (
          <StepInspector
            step={step}
            editable={editable}
            onChange={(next) => write({ steps: open.steps.map((one) => (one.id === next.id ? next : one)) })}
            onRemove={() => removeStep(step.id)}
            onClose={() => setSelected(null)}
          >
            {editable && (
              <ArrowMaker
                step={step}
                steps={open.steps}
                edges={open.edges}
                onLink={(target) => link(step.id, target)}
              />
            )}
          </StepInspector>
        )}
      </div>
    </>
  );
}

/**
 * Drawing an arrow, without a handle to drag.
 *
 * The app connects two steps by pulling from one node's edge to another's. That
 * gesture wants a graph library and a canvas somebody lives in; here the same
 * fact — *what follows this step* — is said in a sentence, which is also the
 * only thing this page is ever used for.
 */
function ArrowMaker({
  step,
  steps,
  edges,
  onLink
}: {
  step: WorkflowStep;
  steps: WorkflowStep[];
  edges: WorkflowEdge[];
  onLink: (target: string) => void;
}): React.JSX.Element {
  const [target, setTarget] = useState('');
  const free = steps.filter((one) => !edges.some((edge) => edge.source === step.id && edge.target === one.id));

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <select
        value={target}
        onChange={(event) => setTarget(event.target.value)}
        aria-label="Where this step goes next"
        style={{
          flex: 1,
          minWidth: 0,
          height: 32,
          padding: '0 10px',
          border: `1px solid ${color.lineInput}`,
          borderRadius: radius.md,
          background: color.surface,
          fontFamily: 'inherit',
          fontSize: 13,
          color: color.ink
        }}
      >
        <option value="">{free.length ? 'Then goes to…' : 'It already points at every step'}</option>
        {free.map((one) => (
          <option key={one.id} value={one.id}>
            {/* Itself is a real answer: a step that loops back on its own check
                is how a repair is written. */}
            {one.id === step.id ? 'itself, again' : `${steps.indexOf(one) + 1} · ${one.name || 'Untitled step'}`}
          </option>
        ))}
      </select>
      <HeaderButton
        disabled={!target}
        onClick={() => {
          if (!target) return;
          onLink(target);
          setTarget('');
        }}
      >
        Draw it
      </HeaderButton>
    </div>
  );
}
